import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  addToast,
  Button,
  compressImage,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Switch,
} from '@structural-climbing/ui'
import { ProductImage } from './ProductImage'
import type { Product, ProductCategory } from './types'

const ACCEPTED_IMAGES = 'image/png,image/jpeg,image/webp'

interface Props {
  isOpen: boolean
  /** Null creates a product. */
  editTarget: Product | null
  categories: ProductCategory[]
  /** The category a new product starts in: the one whose button was pressed. */
  defaultCategoryId: string | null
  currency: string
  onClose: () => void
  onSaved: () => void
}

export function ProductFormModal({
  isOpen,
  editTarget,
  categories,
  defaultCategoryId,
  currency,
  onClose,
  onSaved,
}: Props) {
  const { t } = useTranslation()
  const { getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string
  const fileInput = useRef<HTMLInputElement>(null)

  const [name, setName] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [sellPrice, setSellPrice] = useState('')
  const [buyPrice, setBuyPrice] = useState('')
  const [stock, setStock] = useState('0')
  const [onSale, setOnSale] = useState(true)
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return
    setName(editTarget?.name ?? '')
    setCategoryId(editTarget?.category_id ?? defaultCategoryId ?? categories[0]?.id ?? '')
    setSellPrice(editTarget ? String(editTarget.sell_price) : '')
    setBuyPrice(editTarget?.buy_price != null ? String(editTarget.buy_price) : '')
    setStock(String(editTarget?.stock ?? 0))
    setOnSale(editTarget?.is_active ?? true)
    setFile(null)
    setError(null)
    // The category list is only read when the form opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editTarget, defaultCategoryId])

  // The chosen file, shown before it is uploaded.
  useEffect(() => {
    if (!file) {
      setPreview(null)
      return
    }
    const url = URL.createObjectURL(file)
    setPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  async function uploadPicture(productId: string, token: string): Promise<boolean> {
    if (!file) return true
    try {
      const form = new FormData()
      form.append('file', await compressImage(file, 1024 * 1024, 1200))
      form.append('thumbnail', await compressImage(file, 50 * 1024, 300))
      const res = await fetch(`${apiUrl}/products/${productId}/image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      })
      return res.ok
    } catch {
      return false
    }
  }

  async function handleSave() {
    const sell = Number(sellPrice)
    const buy = buyPrice.trim() === '' ? null : Number(buyPrice)
    const count = Number(stock)
    if (!name.trim()) return setError(t('products.nameRequired'))
    if (!categoryId) return setError(t('products.categoryRequired'))
    if (sellPrice.trim() === '' || !Number.isFinite(sell) || sell < 0)
      return setError(t('products.priceRequired'))
    if (buy !== null && (!Number.isFinite(buy) || buy < 0))
      return setError(t('products.priceRequired'))
    if (!Number.isInteger(count)) return setError(t('products.stockInvalid'))

    setSaving(true)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(
        editTarget ? `${apiUrl}/products/${editTarget.id}` : `${apiUrl}/products`,
        {
          method: editTarget ? 'PATCH' : 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: name.trim(),
            category_id: categoryId,
            sell_price: sell,
            buy_price: buy,
            stock: count,
            is_active: onSale,
          }),
        },
      )
      if (!res.ok) {
        setError(t('products.errorSaveProduct'))
        return
      }
      const productId = editTarget?.id ?? ((await res.json()) as { id: string }).id

      // The product exists either way. A failed picture is said out loud and
      // can be tried again from the edit form; it does not undo the save.
      if (!(await uploadPicture(productId, token))) {
        addToast({ title: t('products.pictureFailed'), color: 'warning' })
      }
      onSaved()
      onClose()
    } catch {
      setError(t('products.errorSaveProduct'))
    } finally {
      setSaving(false)
    }
  }

  const hasPicture = preview !== null || !!editTarget?.image_url

  return (
    <Modal
      isOpen={isOpen}
      size="2xl"
      scrollBehavior="inside"
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader>
              {editTarget ? t('products.editProduct') : t('products.newProduct')}
            </ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-4">
                  {preview ? (
                    <img
                      src={preview}
                      alt={t('products.picture')}
                      className="h-20 w-20 shrink-0 rounded-lg object-cover"
                    />
                  ) : (
                    <ProductImage
                      name={name || '?'}
                      imageUrl={editTarget?.image_url ?? null}
                      className="h-20 w-20 shrink-0 rounded-lg"
                    />
                  )}
                  <input
                    ref={fileInput}
                    type="file"
                    accept={ACCEPTED_IMAGES}
                    className="hidden"
                    aria-label={t('products.picture')}
                    onChange={(e) => {
                      setFile(e.target.files?.[0] ?? null)
                      e.target.value = ''
                    }}
                  />
                  <Button variant="flat" onPress={() => fileInput.current?.click()}>
                    {hasPicture ? t('products.replacePicture') : t('products.uploadPicture')}
                  </Button>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <Input
                    autoFocus={!editTarget}
                    label={t('products.productName')}
                    placeholder={t('products.productNamePlaceholder')}
                    value={name}
                    onValueChange={setName}
                    maxLength={120}
                  />
                  <Select
                    label={t('products.category')}
                    selectedKeys={categoryId ? [categoryId] : []}
                    disallowEmptySelection
                    onSelectionChange={(keys) => {
                      const next = Array.from(keys)[0] as string | undefined
                      if (next) setCategoryId(next)
                    }}
                  >
                    {categories.map((category) => (
                      <SelectItem key={category.id} textValue={category.name}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </Select>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    label={t('products.sellPrice')}
                    endContent={<span className="text-sm text-foreground/50">{currency}</span>}
                    value={sellPrice}
                    onValueChange={setSellPrice}
                  />
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    label={t('products.buyPrice')}
                    description={t('products.buyPriceHint')}
                    endContent={<span className="text-sm text-foreground/50">{currency}</span>}
                    value={buyPrice}
                    onValueChange={setBuyPrice}
                  />
                  <Input
                    type="number"
                    step="1"
                    inputMode="numeric"
                    label={t('products.stock')}
                    description={t('products.stockHint')}
                    value={stock}
                    onValueChange={setStock}
                  />
                </div>

                {editTarget && (
                  <Switch isSelected={onSale} onValueChange={setOnSale}>
                    <span className="text-sm">{t('products.onSale')}</span>
                  </Switch>
                )}

                {error && <p className="text-sm text-danger">{error}</p>}
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="flat" onPress={onClose} isDisabled={saving}>
                {t('common.cancel')}
              </Button>
              <Button color="primary" isLoading={saving} onPress={() => void handleSave()}>
                {t('common.save')}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  )
}
